"use client";
/**
 * The generated plan: a named session, its phases in the order they are run,
 * and one prescription per slot.
 *
 * A plan is not a set of search results and is not drawn as one. It is ONE
 * object with an internal running order, so it renders as a single card whose
 * body is the phase list — and the phases are only drawn where the plan
 * actually filled them. The captured plan carries a warm-up and a main block
 * and an empty cool-down, because the request asked for no cool-down; a
 * `Cool-down` heading over nothing would read as the vendor failing to answer.
 *
 * Each slot renders as a list row rather than a card: what distinguishes one
 * slot from the next is its order, its title and its prescription, and all
 * three fit on a line. The slot's own movement is reachable by pressing it —
 * the plan is a table of contents, not the contents.
 */
import type { CSSProperties, ReactElement } from "react";
import {
  CresultcardFactLine,
  CresultcardListRow,
  CresultcardSectionHeader,
  CresultcardStack,
  CresultcardSubline,
  CRC_TEXT_FAINT,
  CRC_TILE_INSET,
  CRC_TYPE_STYLE,
  CresultcardDensity,
  CresultcardStackDensity,
  CresultcardSublineSize,
  CresultcardTextStep,
  CresultcardTilePadding,
  CresultcardTitleLines,
  type CresultcardFactLineEntry,
} from "@/components/ui/Cresultcard";
import {
  CWORKOUTCARD_MINUTES_LABEL,
  CWORKOUTCARD_MOVEMENTS_NOUN,
  C_WORKOUT_PLAN_DROP_ORDER,
  C_WORKOUT_PLAN_MODIFIER,
  C_WORKOUT_PLAN_VARIANT,
} from "./cworkoutcard.constants";
import {
  cworkoutcardPlanPhases,
  cworkoutcardPrescriptionLine,
  cworkoutcardVendorWords,
} from "./cworkoutcard.model";
import { CworkoutcardShell } from "./CworkoutcardShell";
import {
  CworkoutcardPlanFact,
  type CworkoutcardPlanData,
} from "./cworkoutcard.types";

const CARD_INSET = CRC_TILE_INSET[CresultcardTilePadding.Regular];
const MUSCLE_SEPARATOR = ", ";

const TITLE_STYLE: CSSProperties = {
  ...CRC_TYPE_STYLE[CresultcardTextStep.Lead],
  margin: 0,
};
const SUMMARY_STYLE: CSSProperties = {
  ...CRC_TYPE_STYLE[CresultcardTextStep.Meta],
  flex: "0 0 auto",
  color: CRC_TEXT_FAINT,
};

interface CworkoutcardPlanProps {
  readonly plan: CworkoutcardPlanData;
}

export function CworkoutcardPlan({
  plan,
}: CworkoutcardPlanProps): ReactElement {
  const phases = cworkoutcardPlanPhases(plan);
  // A fact the plan did not state is dropped rather than printed empty: a
  // header that says nothing about its length is honest, one that says `null`
  // is not.
  const facts: readonly CresultcardFactLineEntry[] = [
    ...(plan.estimatedMinutes == null
      ? []
      : [
          {
            key: CworkoutcardPlanFact.Minutes,
            value: `${String(plan.estimatedMinutes)} ${CWORKOUTCARD_MINUTES_LABEL}`,
          },
        ]),
    ...(plan.difficulty == null
      ? []
      : [
          {
            key: CworkoutcardPlanFact.Difficulty,
            value: cworkoutcardVendorWords(plan.difficulty),
          },
        ]),
    {
      key: CworkoutcardPlanFact.Muscles,
      value: plan.muscleGroups
        .map(cworkoutcardVendorWords)
        .join(MUSCLE_SEPARATOR),
    },
    {
      key: CworkoutcardPlanFact.Movements,
      value: `${String(plan.slots.length)} ${CWORKOUTCARD_MOVEMENTS_NOUN}`,
    },
  ];

  return (
    <CworkoutcardShell
      density={CresultcardDensity.Card}
      head={
        <div
          style={{ minWidth: 0, padding: CARD_INSET }}
        >
          <CresultcardStack
            density={CresultcardStackDensity.Tight}
          >
            <h3
              style={TITLE_STYLE}
            >
              {plan.name}
            </h3>
            <CresultcardFactLine
              dropOrder={C_WORKOUT_PLAN_DROP_ORDER}
              facts={facts}
            />
          </CresultcardStack>
        </div>
      }
      modifier={C_WORKOUT_PLAN_MODIFIER}
      variant={C_WORKOUT_PLAN_VARIANT}
      body={
        <div
          style={{
            minWidth: 0,
            paddingInline: CARD_INSET,
            paddingBlockEnd: CARD_INSET,
          }}
        >
          <CresultcardStack
            density={CresultcardStackDensity.Regular}
          >
            {phases.map((group) => (
              <CresultcardStack
                density={CresultcardStackDensity.Snug}
                key={group.phase}
              >
                <CresultcardSectionHeader
                  title={group.phase}
                  trailing={
                    <span
                      style={SUMMARY_STYLE}
                    >
                      {`${String(group.slots.length)} ${CWORKOUTCARD_MOVEMENTS_NOUN}`}
                    </span>
                  }
                />
                {group.slots.map((slot, index) => (
                  <CresultcardListRow
                    key={slot.exercise.key}
                    onLast={index === group.slots.length - 1}
                    ordinal={slot.exercise.prescription?.order ?? index + 1}
                    subtitle={
                      <CresultcardSubline
                        size={CresultcardSublineSize.Small}
                      >
                        {cworkoutcardVendorWords(slot.exercise.muscleGroup)}
                      </CresultcardSubline>
                    }
                    title={slot.exercise.title}
                    titleLines={CresultcardTitleLines.Two}
                    value={
                      slot.exercise.prescription == null
                        ? undefined
                        : cworkoutcardPrescriptionLine(
                            slot.exercise.prescription,
                          )
                    }
                  />
                ))}
              </CresultcardStack>
            ))}
          </CresultcardStack>
        </div>
      }
    />
  );
}
